import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

// teach_project / word-pool-app の LanguageSwitcher を踏襲した言語切替ドロップダウン。
// 選択言語は localStorage('preferredLanguage') に保存し、i18n の LanguageDetector が
// 起動時に同キーを読むため、リロードしても選択が維持される。
const LANGUAGES = [
  { code: "ja", name: "日本語", flag: "🇯🇵" },
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
];

export default function LanguageSwitcher() {
  const { t, i18n } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const currentLanguage =
    LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem("preferredLanguage", langCode);
    setIsOpen(false);
    setTimeout(() => {
      buttonRef.current?.focus();
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setIsOpen(true);
        setSelectedIndex(
          LANGUAGES.findIndex((lang) => lang.code === i18n.language)
        );
      }
      return;
    }

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % LANGUAGES.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(
          (prev) => (prev - 1 + LANGUAGES.length) % LANGUAGES.length
        );
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        handleLanguageChange(LANGUAGES[selectedIndex].code);
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className="flex items-center gap-2 cursor-pointer"
        style={{
          padding: "6px 10px",
          borderRadius: 8,
          background: "transparent",
          border: "none",
          transition: "background 0.2s",
        }}
        aria-label={t("changeLanguage")}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onMouseEnter={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.background =
            "rgba(44,95,79,0.07)")
        }
        onMouseLeave={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.background =
            "transparent")
        }
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>
          {currentLanguage.flag}
        </span>
        <span
          className="hidden sm:inline"
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            color: "#2C5F4F",
          }}
        >
          {currentLanguage.name}
        </span>
        <i
          className="ri-arrow-down-s-line"
          style={{
            fontSize: 16,
            color: "#2C5F4F",
            transition: "transform 0.2s",
            transform: isOpen ? "rotate(180deg)" : "none",
          }}
        />
      </button>

      {isOpen && (
        <div
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            marginTop: 8,
            width: 168,
            background: "#ffffff",
            borderRadius: 10,
            boxShadow: "0 8px 28px rgba(44,95,79,0.15)",
            border: "1px solid rgba(44,95,79,0.1)",
            padding: "6px 0",
            zIndex: 200,
          }}
        >
          {LANGUAGES.map((lang, index) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleLanguageChange(lang.code)}
              onMouseEnter={() => setSelectedIndex(index)}
              role="menuitem"
              className="w-full flex items-center gap-3 cursor-pointer"
              style={{
                padding: "9px 16px",
                background:
                  selectedIndex === index
                    ? "rgba(44,95,79,0.06)"
                    : "transparent",
                border: "none",
                textAlign: "left",
                transition: "background 0.15s",
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>{lang.flag}</span>
              <span
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#1A1A1A",
                }}
              >
                {lang.name}
              </span>
              {i18n.language === lang.code && (
                <i
                  className="ri-check-line"
                  style={{ color: "#2C5F4F", marginLeft: "auto", fontSize: 15 }}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
